import {noop} from '@benchling/js-utils';
import React, {useState} from 'react';

import {ArchiveRecord} from 'archive/ArchiveConstants';
import BenchlingConfig from 'BenchlingConfig';
import {redirectForDownload} from 'BenchlingUtil';
import {ResultsTable} from 'notebook/integrations/results/ResultsTableConstants';
import {EditorPropertiesContext} from 'notebook/notes/core-ui/NoteContext';
import getIsLatestForClass from 'notebook/notes/util/getOpenVersionForClass';
import ManagedColumnsSimpleTableEditable from 'notebook/simple-tables/ManagedColumnsSimpleTableEditable';
import {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {useActionFunctionContext} from 'notebook/structured-tables/action-functions/ActionFunctionContext';
import {
  CreateDatasetButton,
  handleClickActionButton as handleClickCreateDatasetButton,
} from 'notebook/structured-tables/action-functions/createDatasetActionFunction';
import TableWithMissingSchema from 'notebook/structured-tables/components/TableWithMissingSchema';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  LiveRowMetadata,
  StructuredTable,
  StructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import getTablePropsFromActionFunctions from 'notebook/structured-tables/util/getActionFunctions';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableIndicatorStatus} from 'notebook/tables/errors/TableErrorConstants';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {SimpleDownloadAsCSVButton} from 'notebook/tables/export/DownloadAsCSVButton';
import getPlainTextRawTable from 'notebook/tables/export/getPlainTextRawTable';
import {TableHeaderControlsArgs} from 'notebook/tables/TableConstants';
import {fetchOversizedNLADTable} from 'results/assays/AssayRecordActions';
import {MODEL_NAMES} from 'results/ResultsNamingConstants';
import APIUtil from 'util/APIUtil';
import * as ExportUtil from 'util/ExportUtil';
import {initiateTextDownload} from 'util/TextToBlobUtil';

import labAutoResultsTableToTable from './labAutoResultsTableToTable';
import useHasOutOfDateResults from './useHasOutOfDateResults';

interface ResultsTableProps {
  resultsTable: ResultsTable;
  getIdFromCid?: (cid: string) => string | number | undefined;
  automationOutputFileArchiveRecord?: ArchiveRecord | null;
  viewer?: boolean;
}

export default function ResultsTableView(props: ResultsTableProps): JSX.Element {
  const [isCSVDownloadDisabled, setCSVDownloadDisabled] = useState(false);
  const actionFunctionContext = useActionFunctionContext();
  const hasOutOfDateResults = useHasOutOfDateResults(props.resultsTable);

  function renderDownloadAsCSVButton(): JSX.Element {
    return (
      <SimpleDownloadAsCSVButton disabled={isCSVDownloadDisabled} onDownloadAsCSV={() => onDownloadAsCSV()} />
    );
  }

  function renderCreateDatasetFromOversizedTableButton(fileId?: string): JSX.Element {
    async function structuredTableToPlainTextTable<TTableMetadata, TRowMetadata>(args: {
      structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
      structuredTableAdapter: StructuredTableAdapter<TTableMetadata, TRowMetadata>;
      computedTable: Array<Array<EvaluatedCell>>;
      liveRowMetadataByRow?: Array<LiveRowMetadata>;
    }): Promise<Array<Array<string>>> {
      const oversizedTable = await fetchOversizedNLADTable(
        props.getIdFromCid!(props.resultsTable.cid) as string
      );
      const {table} = labAutoResultsTableToTable(props.resultsTable);
      const columnLabels = table.columnLabels as Array<string>;
      return getPlainTextRawTable({
        table: oversizedTable.rows.map((row) => row.cells),
        headerRow: columnLabels,
        useIds: true,
      });
    }

    const isDisabled =
      fileId == null || APIUtil.isEntryOrSubOrWorkflowTemplate(fileId) || !getIsLatestForClass(fileId);

    const clickButtonArgs = {
      entryOrTemplateId: fileId ?? null,
      structuredTable: props.resultsTable,
      structuredTableAdapter: getStructuredTableAdapters()[props.resultsTable.kind],
      computedTable: [], // TODO: This works for now since the backend does not actually use the computed table, but will need to be fixed once we start using it
      actionFunctionContext,
      structuredTableToPlainTextTable,
    };

    return (
      <CreateDatasetButton
        disabled={isDisabled}
        handleClickActionButton={(actionType) => {
          handleClickCreateDatasetButton(clickButtonArgs, actionType);
        }}
      />
    );
  }

  async function onDownloadAsCSV(): Promise<void> {
    setCSVDownloadDisabled(true);
    let cells: Array<Array<Cell>>;
    let downloadUrl = '';
    const {table} = labAutoResultsTableToTable(resultsTable);
    const columnLabels = table.columnLabels as Array<string>;
    try {
      if (resultsTable.rows.length) {
        cells = table.table;
        const exportedRows = getPlainTextRawTable({table: cells, headerRow: columnLabels});
        initiateTextDownload(ExportUtil.formatRows(exportedRows, ','), `${resultsTable.name}.csv`);
      } else {
        const oversizedTable = await fetchOversizedNLADTable(props.getIdFromCid!(resultsTable.cid) as string);
        downloadUrl = oversizedTable.url;
        if (downloadUrl.length) {
          redirectForDownload(downloadUrl);
        }
      }
    } finally {
      setCSVDownloadDisabled(false);
    }
  }

  function renderHeaderControls(args: TableHeaderControlsArgs, fileId: string | undefined): JSX.Element {
    if (args.isCollapsed) {
      return renderCollapsedHeaderControls(fileId);
    }
    const {rightHeaderControls} = getTablePropsFromActionFunctions({
      entryId: fileId ?? null,
      structuredTable: props.resultsTable,
      adapter: getStructuredTableAdapters()[props.resultsTable.kind],
      computedTable: args.computedTable,
      locked: false,
      updateHandler: noop,
      performTableOp: noop,
      actionFunctionContext,
      isFilterStale: false,
    });

    return (
      <>
        {rightHeaderControls(args)}
        {renderDownloadAsCSVButton()}
      </>
    );
  }

  const {resultsTable} = props;
  const configName = resultsTable.metadata.configName;
  const schema = resultsTable.schemas[0];

  if (schema == null) {
    return (
      <TableWithMissingSchema
        icon="table"
        indentation={0}
        tableName={MODEL_NAMES.Result}
        selectable={false}
      />
    );
  }

  const {table, widths} = labAutoResultsTableToTable(resultsTable);
  const isOversized = !resultsTable.rows.length;

  if (props.automationOutputFileArchiveRecord) {
    table.name = `${table.name} (${props.automationOutputFileArchiveRecord.purpose})`;
  }

  function renderCollapsedHeaderControls(fileId?: string): JSX.Element {
    if (!isOversized || !BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS) {
      return renderDownloadAsCSVButton();
    }
    return (
      <>
        {renderCreateDatasetFromOversizedTableButton(fileId)}
        {renderDownloadAsCSVButton()}
      </>
    );
  }

  return (
    <EditorPropertiesContext.Consumer>
      {({fileId}) => (
        <ManagedColumnsSimpleTableEditable
          {...standaloneProps}
          fileId={fileId ?? null}
          cid={resultsTable.cid}
          key={configName}
          locked={true}
          tableStatus={hasOutOfDateResults ? TableIndicatorStatus.IMMUTABLE : undefined}
          canAddRemoveColumns={false}
          headerControls={(args) => renderHeaderControls(args, fileId)}
          tableLabel={MODEL_NAMES.Result}
          enableFormulas={false}
          forceCollapsed={isOversized || props.automationOutputFileArchiveRecord != null}
          forceRowCount={isOversized ? resultsTable.metadata.numResults : undefined}
          hideFormulaBox={true}
          icon="table"
          isCollapsible={true}
          isLeftAlignColumnHeader={true}
          initialColumnWidths={widths}
          tableWithoutColumnWidths={table}
          isTableHeaderFaded={Boolean(props.automationOutputFileArchiveRecord)}
          dataTestAttrs={{component: 'ResultsTableView', element: 'root'}}
          viewer={props.viewer}
        />
      )}
    </EditorPropertiesContext.Consumer>
  );
}
